<script setup>
import { computed, ref, watch } from "vue";
import moment from "moment";
import { storeToRefs } from "pinia";
import useStore from "../store";
import { bookingItemsAreTooClose, isEnabledFlag } from "../helpers";
import AlertError from "./AlertError.vue";
import SectionTitle from "./SectionTitle.vue";
import Session from "./Session.vue";

const props = defineProps({
  item: {
    type: Object,
    default: null,
  },
  itemInd: {
    type: [String, Number],
    default: null,
  },
  booking: {
    type: Object,
    default: null,
  },
  bookingHasPastItems: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["close", "confirm"]);

const store = useStore();
const { attractions } = storeToRefs(store);

const selectedTime = ref("");
const selectedAvailability = ref({});
const selectedPrices = ref([]);

const normalizeLabel = (label) => String(label ?? "").trim().toLowerCase();

const attraction = computed(() => {
  if (!props.item?.productCode) {
    return null;
  }

  return (
    attractions.value.find(
      (current) => current.rezdy_retail_product_code === props.item.productCode
    ) ?? null
  );
});

const currentTime = computed(() => {
  const time = props.item?.startTimeLocal ?? props.item?.startTime ?? false;

  return time ? moment(time).format("dddd Do MMMM YYYY, h:mma") : "";
});

const moveBookingCutoffMinutes = computed(() =>
  Number(attraction.value?.move_booking_cutoff_minutes ?? 0)
);

const moveBookingsEnabled = computed(() =>
  isEnabledFlag(attraction.value?.enabled_move_bookings)
);

const bookingIsWithinMoveCutoff = computed(() => {

  const time = props.item?.startTimeLocal ?? props.item?.startTime ?? null;

  // forest adventures
  if (props.item?.productCode === 'P5BLY8'){
    const moveCutoff = moment(time)
        .startOf("day")
        .subtract(1, "day")
        .hour(17)
        .minute(0)
        .second(0)
        .millisecond(0);

    return moment().isSameOrAfter(moveCutoff);
  }

  if (!time || moveBookingCutoffMinutes.value <= 0) {
    return false;
  }

  return moment(time).diff(moment(), "minutes", true) <= moveBookingCutoffMinutes.value;
});

const quantities = computed(() => props.item?.quantities ?? []);

const totalParticipants = computed(() =>
  quantities.value.reduce((total, quantity) => total + Number(quantity?.value ?? 0), 0)
);

const currentPriceTotal = computed(() =>
  quantities.value.reduce(
    (total, quantity) =>
      total + Number(quantity?.value ?? 0) * Number(quantity?.optionPrice ?? 0),
    0
  )
);

const selectedTimeChanged = computed(() => {
  const current = props.item?.startTimeLocal ?? props.item?.startTime ?? "";
  return !!selectedTime.value && selectedTime.value !== current;
});

const bookingHasDistanceConflict = computed(() => {
  if (!selectedTime.value || props.itemInd === null || props.itemInd === undefined) {
    return false;
  }

  const items = (props.booking?.items ?? []).map((currentItem, index) => ({
    ...currentItem,
    time:
      index === Number(props.itemInd)
        ? selectedTime.value
        : currentItem?.startTimeLocal ?? currentItem?.startTime ?? null,
  }));


  return bookingItemsAreTooClose(items);
});

const seatsRequired = computed(() => {
  if (!Array.isArray(selectedAvailability.value?.priceOptions)) {
    return totalParticipants.value;
  }

  return quantities.value.reduce((total, quantity) => {
    const option = selectedAvailability.value.priceOptions.find(
      (priceOption) =>
        normalizeLabel(priceOption?.label) === normalizeLabel(quantity?.optionLabel)
    );

    return total + Number(option?.seatsUsed ?? 1) * Number(quantity?.value ?? 0);
  }, 0);
});

const validationMessage = computed(() => {
  if (!props.item) {
    return "This booking item can’t be moved right now.";
  }

  if (props.bookingHasPastItems) {
    return "This booking can’t be changed because one or more booking times are already in the past.";
  }

  if (!attraction.value) {
    return "We couldn’t match this booking to an available experience.";
  }

  if (!moveBookingsEnabled.value) {
    return "This booking can’t be moved online.";
  }

  if (bookingIsWithinMoveCutoff.value) {
    return `Your current session start time is too soon to be moved online. Please contact our team on (08) 9757 7411 to discuss your options.`;
  }

  if (!selectedTime.value || !selectedAvailability.value?.startTimeLocal) {
    return "Choose a new date and time to continue.";
  }

  if (!selectedTimeChanged.value) {
    return "Choose a different date or time to move this booking.";
  }

  if (bookingHasDistanceConflict.value) {
    return "This time is too close to another attraction in your booking. Please allow more time for travel between activities.";
  }

  const priceOptions = selectedAvailability.value?.priceOptions ?? [];
  const totalForNewTime = quantities.value.reduce((total, quantity) => {
    const priceOption = priceOptions.find(
      (current) => normalizeLabel(current?.label) === normalizeLabel(quantity?.optionLabel)
    );

    if (!priceOption) {
      return total;
    }

    return total + Number(quantity?.value ?? 0) * Number(priceOption?.price ?? 0);
  }, 0);

  for (const quantity of quantities.value) {
    const priceOption = priceOptions.find(
      (current) => normalizeLabel(current?.label) === normalizeLabel(quantity?.optionLabel)
    );

    if (!priceOption) {
      return `This time doesn’t support your current ${quantity.optionLabel} tickets.`;
    }

    if (Math.abs(Number(priceOption?.price ?? 0) - Number(quantity?.optionPrice ?? 0)) > 0.009) {
      return "This time changes the ticket price, so it can’t be selected.";
    }
  }

  if (Math.abs(totalForNewTime - currentPriceTotal.value) > 0.009) {
    return "This time changes the booking total, so it can’t be selected.";
  }

  if (Number(selectedAvailability.value?.seatsAvailable ?? 0) < seatsRequired.value) {
    return `This time only has ${selectedAvailability.value?.seatsAvailable ?? 0} seats available for your booking.`;
  }

  return "";
});

const close = () => {
  if (props.saving) {
    return;
  }

  emit("close");
};

const confirm = () => {
  if (validationMessage.value || !attraction.value) {
    return;
  }

  emit("confirm", {
    item: props.item,
    itemInd: props.itemInd,
    attraction: attraction.value,
    time: selectedTime.value,
    availability: selectedAvailability.value,
  });
};

watch(
  () => props.item,
  (item) => {
    const currentTimeValue = item?.startTimeLocal ?? item?.startTime ?? "";

    selectedTime.value = currentTimeValue;
    selectedAvailability.value = {};
    selectedPrices.value = [];
  },
  { immediate: true }
);

</script>

<template>
  <div class="w-full px-4 pb-8 pt-2 lg:px-0 lg:pb-12">
    <div class="mx-auto w-full max-w-6xl rounded-[32px] bg-[#F4F1EB] p-6 text-dark lg:p-10">
      <div class="flex items-start justify-between gap-4 border-b border-primary/30 pb-6">
        <div>
          <div class="text-sm text-dark/60">
            Manage booking
          </div>
          <h2 class="mt-2 text-3xl font-bold lg:text-4xl">Move booking time</h2>
          <p class="mt-3 max-w-2xl text-lg text-dark/70">
            Pick a new date and time for this booking. Your ticket quantity and price stays the same.
          </p>
        </div>

        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full border-2 border-dark bg-white px-5 py-2.5 font-bold text-dark"
          :disabled="saving"
          @click="close"
        >
          Back to order
        </button>
      </div>

      <div class="grid gap-8 pt-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div class="rounded-[28px] bg-white px-6 py-2 lg:px-8">
          <Session
            v-if="attraction"
            v-model="selectedTime"
            v-model:availability="selectedAvailability"
            v-model:prices="selectedPrices"
            :attraction="attraction"
            :product-code="item?.productCode ?? false"
            :required-seats="totalParticipants"
            :submitted="true"
            :number="1"
          />

          <AlertError v-else>
            This booking item can’t be moved because its experience details aren’t available.
          </AlertError>
        </div>

        <div class="rounded-[28px] bg-light px-6 py-6 lg:px-8">
          <SectionTitle :number="2" class="mt-4">
            Booking details
          </SectionTitle>

          <div class="space-y-5">
            <div>
              <div class="text-sm text-dark/50">
                Attraction
              </div>
              <div class="mt-2 text-lg font-semibold">{{ attraction.retail_title }}</div>
            </div>
            <div>
              <div class="text-sm text-dark/50">
                Current time
              </div>
              <div class="mt-2 text-lg font-semibold">{{ currentTime }}</div>
            </div>
            <div>
              <div class="text-sm text-dark/50">
                Tickets
              </div>
              <ul class="mt-3 space-y-2">
                <li
                  v-for="quantity in quantities"
                  :key="`${item?.id}-${quantity.optionLabel}`"
                  class="flex items-center justify-between gap-4 text-base"
                >
                  <span>{{ `${quantity.value} x ${quantity.optionLabel}` }}</span>
                  <span class="font-semibold">{{ `$${(Number(quantity.optionPrice ?? 0) * Number(quantity.value ?? 0)).toFixed(2)}` }}</span>
                </li>
              </ul>
            </div>

            <div class="flex items-center justify-between border-t border-dotted border-primary pt-4 text-lg">
              <span class="font-semibold">Locked total</span>
              <span class="font-bold">{{ `AUD $${currentPriceTotal.toFixed(2)}` }}</span>
            </div>

            <AlertError v-if="error">
              {{ error }}
            </AlertError>

            <AlertError v-else-if="validationMessage">
              {{ validationMessage }}
            </AlertError>
          </div>
        </div>
      </div>

      <div class="mt-8 flex flex-col-reverse gap-3 border-t border-primary/30 pt-6 sm:flex-row sm:justify-end">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full border-2 border-dark bg-white px-6 py-3 font-bold text-dark"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </button>

        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full bg-dark px-6 py-3 font-bold text-light disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="saving || !!validationMessage"
          @click="confirm"
        >
          {{ saving ? "Moving booking..." : "Move booking time" }}
        </button>
      </div>
    </div>
  </div>
</template>
